import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import "rsuite/dist/rsuite.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import "./bootstrap.css";
import "boxicons/css/boxicons.css";
import "../node_modules/swiper/swiper-bundle.min.css";
import "./App.css";
import "./style/globals.scss";
import "./utilities.css";
import "react-toastify/dist/ReactToastify.css";

import Home from "./pages/Home";
import Login from "./pages/Login";

import Stamps from "./pages/Stamps/Stamps";
import StampsAdd from "./pages/Stamps/StampsAdd";
import StampsEdit from "./pages/Stamps/StampsEdit";
import StampsView from "./pages/Stamps/StampsView";

import News from "./pages/News/News";
import NewsAdd from "./pages/News/NewsAdd";
import NewsEdit from "./pages/News/NewsEdit";
import NewsView from "./pages/News/NewsView";

import Users from "./pages/Users/Users";
import UsersView from "./pages/Users/UsersView";

import { ToastContainer } from "react-toastify";
import Error from "./pages/Error/Error";
import NewWebView from "./pages/NewWebview";

import KML from "./pages/KML";
import KMLiOS from "./pages/KMLiOS";
import MapWebView from "./pages/MapWebView";
import Demo from "./Demo";
import ContactUs from "./ContactUs";
import { lazy, Suspense, useEffect } from "react";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import Loading from "./Component/Loading";

import { useAuth } from "./Auth/AuthContext";

/**
 * The Blog screens are code-split: the rich-text editor they use (CKEditor)
 * is by far the heaviest dependency in the panel, and loading it eagerly would
 * slow the dashboard, Stamps, News and Users pages down for no benefit. These
 * chunks are fetched the first time an admin opens a Blog route.
 */
const Blog = lazy(() => import("./pages/Blog/Blog"));
const BlogAdd = lazy(() => import("./pages/Blog/BlogAdd"));
const BlogEdit = lazy(() => import("./pages/Blog/BlogEdit"));
const BlogView = lazy(() => import("./pages/Blog/BlogView"));
const BlogCategories = lazy(() => import("./pages/Blog/Categories"));

/**
 * Renders children only when the user is authenticated.
 * Shows a full-screen loader while the silent refresh is in progress
 * so we never flash the login page on a valid session.
 */
const ProtectedRoute = ({ redirectPath = "/" }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  if (!isInitialized) return <Loading fullloader="full-preloader" />;
  if (!isAuthenticated) return <Navigate to={redirectPath} replace />;
  return <Outlet />;
};

/**
 * Prevents authenticated users from reaching the login page.
 */
const PrivateRoute = ({ redirectPath = "/Home" }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  if (!isInitialized) return <Loading fullloader="full-preloader" />;
  if (isAuthenticated) return <Navigate to={redirectPath} replace />;
  return <Outlet />;
};

function App() {
  const { pathname } = window.location;

  useEffect(() => {
    if (pathname !== "/" && pathname !== "/contact-us") {
      document.body.style.background = "#f5f5f5";
      document.body.classList.add("panel-css");
    }
  }, [pathname]);

  return (
    <>
      <ToastContainer
        position="bottom-right"
        autoClose="500"
        closeOnClick="true"
      />
      <BrowserRouter>
        {/* Covers the lazily-loaded Blog chunks above. */}
        <Suspense fallback={<Loading fullloader="full-preloader" />}>
        <Routes>
          <Route element={<ProtectedRoute />}>
            <Route path="/Home" element={<Home />} />

            <Route path="/Stamps" element={<Stamps />} />
            <Route path="/Stamps/Add" element={<StampsAdd />} />
            <Route path="/Stamps/Edit/:id" element={<StampsEdit />} />
            <Route path="/Stamps/View/:id" element={<StampsView />} />

            <Route path="/News" element={<News />} />
            <Route path="/News/Add" element={<NewsAdd />} />
            <Route path="/News/Edit/:id" element={<NewsEdit />} />
            <Route path="/News/View/:id" element={<NewsView />} />

            <Route path="/Blog" element={<Blog />} />
            <Route path="/Blog/Categories" element={<BlogCategories />} />
            <Route path="/Blog/Add" element={<BlogAdd />} />
            <Route path="/Blog/Edit/:id" element={<BlogEdit />} />
            <Route path="/Blog/View/:id" element={<BlogView />} />

            <Route path="/Users" element={<Users />} />
            <Route path="/Users/View" element={<UsersView />} />
            <Route path="/Users/View/:id" element={<UsersView />} />
          </Route>

          <Route path="/destination/:title" element={<NewWebView />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/terms_of_use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/privacy_policy" element={<PrivacyPolicy />} />
          <Route path="*" element={<Error />} />

          <Route path="/" element={<Demo />} />
          <Route path="/contact-us" element={<ContactUs />} />

          <Route path="/kml_upload" element={<KML />} />
          <Route path="/ios_kml" element={<KMLiOS />} />
          <Route path="/mapwebview" element={<MapWebView />} />
          <Route path="/mapwebview/:id" element={<MapWebView />} />

          <Route element={<PrivateRoute />}>
            <Route path="/login" element={<Login />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
